import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import {
  ArrowUpRight,
  Lightbulb,
  PenLine,
  ClipboardList,
  Clapperboard,
  Wand2,
  Sparkles,
} from "lucide-react";

const stepIcons = [Lightbulb, PenLine, ClipboardList, Clapperboard, Wand2];

const Methodology = () => {
  const { t } = useLang();
  const steps = t("methodology.steps");
  const intro = t("methodology.intro");

  return (
    <div data-testid="methodology-page">
      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/bg/studio.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/85 to-[#0A0A0C]" />
        </div>
        <div className="relative container-x max-w-5xl">
          <div className="overline mb-6">{t("methodology.eyebrow")}</div>
          <h1 className="display-h1">{t("methodology.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl">
            {t("methodology.lead")}
          </p>
        </div>
      </section>

      {/* INTRO (Intersection) */}
      {intro && typeof intro === "object" && (
        <section className="pb-16 md:pb-24">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-4 inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Intersection
              </div>
              <h2 className="display-h2 leading-tight">{intro.heading}</h2>
            </div>
            <div className="lg:col-span-7 space-y-5 border-s-2 border-[#E6A95B]/40 ps-8">
              {Array.isArray(intro.paragraphs) &&
                intro.paragraphs.map((p, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="text-white/75 text-lg md:text-xl leading-relaxed"
                    data-testid={`methodology-intro-p${i + 1}`}
                  >
                    {p}
                  </motion.p>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* STEPS */}
      <section className="py-16 md:py-24 bg-[#0d0d10] border-y border-white/5">
        <div className="container-x max-w-6xl">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">Sections</div>
            <h2 className="display-h2">
              {t("methodology.title")}
            </h2>
          </div>

          <div className="relative">
            {/* vertical spine */}
            <div className="absolute top-0 bottom-0 start-6 md:start-8 w-px bg-gradient-to-b from-transparent via-[#E6A95B]/60 to-transparent" />

            <ul className="space-y-16 md:space-y-24">
              {Array.isArray(steps) &&
                steps.map((s, i) => {
                  const Icon = stepIcons[i] || Lightbulb;
                  return (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{ duration: 0.7, delay: i * 0.05 }}
                      className="relative ps-20 md:ps-28"
                      data-testid={`methodology-step-${i + 1}`}
                    >
                      <div className="absolute start-0 top-0 w-12 md:w-16 h-12 md:h-16 rounded-full bg-[#E6A95B] text-[#0A0A0C] grid place-items-center shadow-[0_0_40px_-10px_rgba(230,169,91,0.7)]">
                        <Icon className="w-6 h-6 md:w-7 md:h-7" />
                      </div>

                      <div className="flex flex-wrap items-baseline gap-4 mb-4">
                        <span className="font-display text-[#E6A95B] text-4xl md:text-5xl leading-none">
                          {s.num}
                        </span>
                        <h3 className="display-h3">{s.title}</h3>
                      </div>

                      <p className="text-white/75 text-lg leading-relaxed max-w-3xl">
                        {s.desc}
                      </p>

                      {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                        <ul className="mt-6 space-y-3 max-w-2xl">
                          {s.bullets.map((b, bi) => (
                            <li
                              key={bi}
                              className="flex gap-3 text-white/70 leading-relaxed"
                              data-testid={`methodology-step-${i + 1}-bullet-${bi + 1}`}
                            >
                              <span className="text-[#E6A95B] shrink-0">—</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  );
                })}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="/bg/studio.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/80 via-[#0A0A0C]/85 to-[#0A0A0C]" />
        </div>
        <div className="relative container-x max-w-3xl mx-auto text-center">
          <h2 className="display-h2 italic">{t("methodology.ctaText")}</h2>
          <div className="mt-10">
            <Link
              to="/contact"
              data-testid="methodology-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("methodology.ctaBtn")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Methodology;
